import { useEffect } from "react";
import clarity from "@microsoft/clarity";

interface ClarityOptions {
  customId?: string;
  customSessionId?: string;
  customPageId?: string;
  friendlyName?: string;
  tags?: Record<string, string | string[]>;
  events?: string[];
  requireConsent?: boolean;
  upgradeSession?: string;
}

/**
 * A custom hook to initialize and configure Microsoft Clarity
 *
 * @param clarityProjectId - Your Microsoft Clarity project ID
 * @param options - Configuration options for Clarity features
 */
const useClarity = (clarityProjectId: string, options?: ClarityOptions) => {
  useEffect(() => {
    if (!clarityProjectId) {
      console.warn("Clarity Project ID is missing");
      return;
    }

    try {
      // Initialize Clarity with your project ID
      clarity.init(clarityProjectId);
      console.log("Microsoft Clarity initialized successfully");

      // Handle user identification if provided
      if (options?.customId) {
        clarity.identify(
          options.customId,
          options.customSessionId,
          options.customPageId,
          options.friendlyName
        );
        console.log("Clarity identify called with customId:", options.customId);
      }

      // Set custom tags if provided
      if (options?.tags && Object.keys(options.tags).length > 0) {
        Object.entries(options.tags).forEach(([key, value]) => {
          clarity.setTag(key, value);
        });
        console.log("Clarity tags set:", options.tags);
      }

      // Track custom events if provided
      if (options?.events && options.events.length > 0) {
        options.events.forEach((eventName) => {
          clarity.event(eventName);
        });
        console.log("Clarity events tracked:", options.events);
      }

      // Handle consent if required
      if (options?.requireConsent !== undefined) {
        clarity.consent(options.requireConsent);
        console.log("Clarity consent set to:", options.requireConsent);
      }

      // Upgrade session if reason provided
      if (options?.upgradeSession) {
        clarity.upgrade(options.upgradeSession);
        console.log(
          "Clarity session upgraded for reason:",
          options.upgradeSession
        );
      }
    } catch (error) {
      console.error("Error initializing Microsoft Clarity:", error);
    }

    // Clean up function
    return () => {
      // No official cleanup method in Clarity API
      // This is a placeholder for any future cleanup needs
    };
  }, [clarityProjectId, options]);
};

/**
 * Track event in Microsoft Clarity
 * Can be used anywhere in the application
 *
 * @param eventName - Name of the event to track
 * @param eventData - Optional data to associate with the event
 */
export const trackEvent = (
  eventName: string,
  eventData?: Record<string, any>
) => {
  try {
    // Track the basic event
    clarity.event(eventName);

    // If there's additional data, set it as temporary tags
    if (eventData && Object.keys(eventData).length > 0) {
      // Add event data as tags with event name as prefix
      Object.entries(eventData).forEach(([key, value]) => {
        // Convert arrays or objects to JSON strings
        const tagValue =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        clarity.setTag(`${eventName}_${key}`, tagValue);
      });

      // For e-commerce events, upgrade the session to prioritize it
      if (
        eventName.includes("add_to_cart") ||
        eventName.includes("purchase") ||
        eventName.includes("checkout") ||
        eventName.includes("view_item")
      ) {
        upgradeSession(eventName);
      }

      // Log the event with data
      console.log(`Clarity event tracked: ${eventName}`, eventData);
    } else {
      console.log(`Clarity event tracked: ${eventName}`);
    }
  } catch (error) {
    console.error(`Error tracking Clarity event ${eventName}:`, error);
  }
};

/**
 * Track e-commerce events specifically for Clarity
 * This function is similar to trackEvent but with e-commerce specific enhancements
 *
 * @param eventName - Name of the e-commerce event
 * @param eventData - E-commerce data to associate with the event
 */
export const trackEcommerceEvent = (
  eventName: string,
  eventData: Record<string, any>
) => {
  try {
    // Track the basic event
    clarity.event(`ecommerce_${eventName}`);

    // Always upgrade e-commerce events to prioritize them
    upgradeSession(`ecommerce_${eventName}`);

    // Set cart ID as a tag if available
    const cartId = localStorage.getItem("cartId");
    if (cartId) {
      clarity.setTag("cart_id", cartId);
    }

    // Set session ID if available
    const sessionId = sessionStorage.getItem("sessionId");
    if (sessionId) {
      clarity.identify(sessionId);
    }

    // Add all event data as tags
    if (eventData) {
      // Add transaction ID as a special tag for purchase events
      if (eventName === "purchase" && eventData.transaction_id) {
        clarity.setTag("transaction_id", eventData.transaction_id);
      }

      // Add value as a special tag
      if (eventData.value) {
        clarity.setTag(`${eventName}_value`, String(eventData.value));
      }

      // Add items as a special tag
      if (eventData.items && Array.isArray(eventData.items)) {
        clarity.setTag(
          `${eventName}_items_count`,
          String(eventData.items.length)
        );
        clarity.setTag(`${eventName}_items`, JSON.stringify(eventData.items));
      }

      // Add all other properties
      Object.entries(eventData).forEach(([key, value]) => {
        if (key !== "items") {
          // Skip items as we've already processed them
          const tagValue =
            typeof value === "object" ? JSON.stringify(value) : String(value);
          clarity.setTag(`${eventName}_${key}`, tagValue);
        }
      });
    }

    console.log(`Clarity e-commerce event tracked: ${eventName}`, eventData);
  } catch (error) {
    console.error(
      `Error tracking Clarity e-commerce event ${eventName}:`,
      error
    );
  }
};

/**
 * Identify user in Clarity
 *
 * @param userId - Unique identifier for the user (will be hashed by Clarity)
 * @param sessionId - Optional custom session ID
 * @param pageId - Optional custom page ID
 * @param friendlyName - Optional friendly name for the user
 */
export const identifyUser = (
  userId: string,
  sessionId?: string,
  pageId?: string,
  friendlyName?: string
) => {
  try {
    clarity.identify(userId, sessionId, pageId, friendlyName);
    console.log(`User identified in Clarity: ${userId}`);
  } catch (error) {
    console.error(`Error identifying user in Clarity:`, error);
  }
};

/**
 * Set a custom tag in Clarity
 *
 * @param key - Tag key
 * @param value - Tag value (string or array of strings)
 */
export const setTag = (key: string, value: string | string[]) => {
  try {
    clarity.setTag(key, value);
    console.log(`Clarity tag set: ${key}=${value}`);
  } catch (error) {
    console.error(`Error setting Clarity tag:`, error);
  }
};

/**
 * Set consent for Clarity tracking
 *
 * @param hasConsent - Whether the user has given consent
 */
export const setConsent = (hasConsent: boolean = true) => {
  try {
    clarity.consent(hasConsent);
    console.log(`Clarity consent set to: ${hasConsent}`);
  } catch (error) {
    console.error(`Error setting Clarity consent:`, error);
  }
};

/**
 * Upgrade session importance in Clarity
 *
 * @param reason - Reason for upgrading the session
 */
export const upgradeSession = (reason: string) => {
  try {
    clarity.upgrade(reason);
    console.log(`Clarity session upgraded: ${reason}`);
  } catch (error) {
    console.error(`Error upgrading Clarity session:`, error);
  }
};

export default useClarity;
